import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition } from '../types';

export interface SwarmAttack {
  kind: 'swarm';
  damage: number;
  cooldownTicks: number;
  fireflies: number;
  night: number;
  turnBeats: number;
  hits: number;
  reach: number;
  range: number;
  speed: number;
  returnSpeed: number;
}

export interface LanterneEffect {
  kind: 'lanterne';
  fireflies: number;
  night: number;
  damage: number;
  reach: number;
  fanDegrees: number;
  range: number;
  speed: number;
  returnSpeed: number;
}

export const VEILLEUR: ClassDefinition = {
  id: 'veilleur',
  name: 'Le Veilleur aux lucioles',
  role: 'Ses lucioles filent piquer les bad vibes et reviennent, plus nombreuses la nuit.',
  color: '#4fc6a0',
  maxHp: 120,
  speed: 7.2,
  radius: 14,
  pickupRadius: 120,
  attack: {
    kind: 'swarm',
    damage: 12,
    cooldownTicks: TICKS_PER_BEAT,
    fireflies: 6,
    night: 2,
    turnBeats: 3,
    hits: 1,
    reach: 220,
    range: 120,
    speed: 16,
    returnSpeed: 12,
  },
  skill: {
    id: 'lanterne-ouverte',
    name: 'Lanterne ouverte',
    description:
      'Tu ouvres ta lanterne et une nuée de lucioles pique les bad vibes devant toi, plus encore la nuit.',
    cooldownTicks: 4 * TICKS_PER_BAR,
    effect: {
      kind: 'lanterne',
      fireflies: 11,
      night: 4,
      damage: 12,
      reach: 220,
      fanDegrees: 120,
      range: 120,
      speed: 16,
      returnSpeed: 12,
    },
  },
  affinityWeaponIds: ['eventails-de-feu'],
};

export const VEILLEUR_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'lanterne-pleine',
    name: 'Lanterne pleine',
    description: 'Une luciole de plus vole avec toi.',
    family: 'class',
    classId: 'veilleur',
    modifiers: [{ stat: 'projectileCountAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Deux lucioles de plus volent avec toi.',
        modifiers: [{ stat: 'projectileCountAdd', add: 2 }],
      },
      legendary: {
        description: 'Trois lucioles de plus volent avec toi.',
        modifiers: [{ stat: 'projectileCountAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'lucioles-vagabondes',
    name: 'Lucioles vagabondes',
    description: 'Tes lucioles piquent une bad vibe de plus en chemin.',
    family: 'class',
    classId: 'veilleur',
    modifiers: [{ stat: 'pierceAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Tes lucioles piquent deux bad vibes de plus en chemin.',
        modifiers: [{ stat: 'pierceAdd', add: 2 }],
      },
      legendary: {
        description: 'Tes lucioles piquent trois bad vibes de plus en chemin.',
        modifiers: [{ stat: 'pierceAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'lanterne-grande-ouverte',
    name: 'Lanterne grande ouverte',
    description: 'Ta spéciale libère deux lucioles de plus.',
    family: 'class',
    classId: 'veilleur',
    modifiers: [{ stat: 'skillCountAdd', add: 2 }],
    rarities: {
      rare: {
        description: 'Ta spéciale libère quatre lucioles de plus.',
        modifiers: [{ stat: 'skillCountAdd', add: 4 }],
      },
      legendary: {
        description: 'Ta spéciale libère six lucioles de plus.',
        modifiers: [{ stat: 'skillCountAdd', add: 6 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'lanterne-vite-rouverte',
    name: 'Lanterne vite rouverte',
    description: 'Ta spéciale revient plus vite.',
    family: 'class',
    classId: 'veilleur',
    modifiers: [{ stat: 'skillCooldownMul', mul: 0.64 }],
    rarities: {
      rare: {
        description: 'Ta spéciale revient bien plus vite.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.36 }],
      },
      legendary: {
        description: 'Ta spéciale revient presque aussitôt.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.16 }],
      },
    },
    maxStacks: 2,
  },
];
